/**
 * Der Ziehgriff der Artikelpositionen (Anlagemaske) - `itemCollectionForReorder`,
 * `reorderItemArray`, `moveItemPosition`, `handleItemReorderKey`, `clearItemDropMarkers`,
 * `startItemDrag`, `previewItemDrop`, `finishItemDrag`, `dropItemPosition`
 * (index.html:9684-9788). Der Griff selbst steht seit dem 2026-08-15-Angleich in
 * `PositionsEditor.tsx` (`className="item-drag-handle"`), aber ohne `onDragStart`/`onDragEnd`/
 * `onKeyDown` - dieses Modul liefert die Handler nach.
 *
 * ================= Bauart: wie `Board/ZeilenUmzug.ts` (Vorlage) =================
 *
 * Kein React-`useState` fuer den Ziehzustand: ein winziges Traegerobjekt (`PositionenZiehenRef`),
 * das der Aufrufer per `useRef` haelt und durchreicht - kein Re-Render je Mausbewegung, die
 * sichtbare Rueckmeldung tragen CSS-Klassen. Reine, exportierte Handler-Funktionen, je ein
 * natives Ereignis. Trennung von Rechnung und Wirkung: dieses Modul RECHNET die neue Reihenfolge
 * aus und gibt sie zurueck (`null` bei einer Nichtoperation); `onAendern(neueListe)` ruft der
 * Aufrufer (`PositionsEditor.tsx`) - der Kanal, den `PositionsEditorProps` bereits fuehrt
 * (`onAendern: (positionen: OrderItem[]) => void`), es braucht keine neue Prop.
 *
 * ================= Woertlich uebernommene Eigenheiten =================
 *
 * 1. `moveItemPosition` ruft `save()` fuer JEDEN scope AUSSER `'new-order'` (index.html:9716) -
 *    fuer die Anlagemaske (der einzige Aufrufer dieses Moduls) entfaellt das Speichern deshalb
 *    ganz: reiner Seitenzustand, kein Netzwerkaufruf.
 * 2. Die Zielindex-Rechnung aus `dropItemPosition` (index.html:9782-9784) 1:1 uebernommen:
 *    `toIndex = zielIndex + (danach ? 1 : 0)`, DANACH `if (von < toIndex) toIndex--`, erst
 *    zuletzt Klemmung auf `[0, laenge-1]`.
 * 3. `previewItemDrop` (index.html:9765) entscheidet ueber die Zeilenmitte:
 *    `danach = clientY >= top + height/2`.
 * 4. `handleItemReorderKey`/`moveItemPosition(..., restoreFocus=true)` (index.html:9719-9723):
 *    NUR der Tastaturweg setzt den Fokus auf den Griff der ZIELzeile zurueck (per `setTimeout`,
 *    wie im Original). Der Ziehweg (`dropItemPosition`) ruft `moveItemPosition` OHNE den vierten
 *    Parameter, also ohne Fokus-Wiederherstellung - hier folgerichtig kein Aequivalent dazu.
 * 5. Die vier CSS-Klassen, die es in `styles/bestand.css` bereits gab und die bislang KEIN `.tsx`
 *    setzte: `item-dragging`/`item-drop-before`/`item-drop-after` auf der `<tr>`, `item-reordering`
 *    auf `document.body` - hier per `classList`, nicht per React-`className`, aus demselben Grund
 *    wie in `ZeilenUmzug.ts`: kein Re-Render je Mausbewegung.
 * 6. `startItemDrag`s Abbruchwache (index.html:9745-9748): fehlt das Element am Index, bricht
 *    `positionZiehenStart` mit `preventDefault()` ab, BEVOR irgendein Zustand gesetzt wird - kein
 *    `ref.index`, keine Klasse. Uebernommen, obwohl in React strukturell kaum erreichbar (der Index
 *    stammt aus derselben Rendering-Closure wie die Zeile selbst) - Vollstaendigkeit statt Verlass
 *    auf diese Annahme.
 * 7. Die `dataTransfer`-Nutzlast `` `${scope}:${index}` `` (index.html:9751) 1:1 als
 *    `` `new-order:${index}` `` - der Scope ist hier immer `'new-order'` (siehe
 *    `positionZielFokussieren` unten). Niemand liest die Nutzlast (die eigentliche Information
 *    reist ueber `PositionenZiehenRef`, siehe jsdom-Falle unten), sie steht nur aus Parallelitaet
 *    zum Bestand hier.
 *
 * ================= Bewusste Abweichung: `raeumeAuf` sucht nur in der Tabelle =================
 *
 * `clearItemDropMarkers` (index.html:9737-9741) raeumt per `document.querySelectorAll` im GANZEN
 * Dokument auf. `raeumeAuf` (unten) scoped stattdessen auf die naechste `<table>` - wie
 * `boardWurzel` in `ZeilenUmzug.ts` fuers Board. Heute gleichwertig (es gibt genau eine
 * Positionstabelle je Anlagemaske, und `item-reordering` bleibt bewusst global auf
 * `document.body`) - scoped ist die vorsichtigere Wahl, falls diese Datei einmal das gemeinsame
 * Modul aus F-2 wird und mehrere Tabellen gleichzeitig im DOM stehen.
 *
 * ================= jsdom-Falle (wortgleiche Ursache wie `ZeilenUmzug.ts`) =================
 *
 * `event.dataTransfer` existiert unter jsdom (`jsdom@29.1.1`, dieses Repo) nicht - jeder Zugriff
 * geht ueber Optional Chaining; die Nutzlast (der gezogene Index) reist zusaetzlich ueber
 * `PositionenZiehenRef`. Unter jsdom fehlt ausserdem `window.DragEvent` selbst (nicht nur
 * `DataTransfer`) - `@testing-library/dom` liefert bei `fireEvent.dragOver(...)` deshalb ein
 * einfaches `Event`, dessen init-Objekt KEIN `clientY` uebernimmt (WebIDL ignoriert unbekannte
 * Dictionary-Felder). Ein Test, der die Zeilenmitte pruefen will, muss `clientY` deshalb NACH dem
 * Erzeugen des Ereignisses direkt zuweisen, nicht ueber das `fireEvent`-init-Objekt.
 *
 * ================= Abweichung vom Bestand: KEIN Toast (Festlegung, nicht vergessen) =================
 *
 * `moveItemPosition` meldet im Bestand `` toast(`Position ${from+1} nach ${to+1} verschoben`) ``
 * (index.html:9718). Der Meldungsweg endet im Port VOR der Anlagemaske: `zeigeMeldung` lebt in
 * `App.tsx`, und weder `AnlagemaskeProps` (`Anlagemaske.tsx`) noch `PositionsEditorProps`
 * (`PositionsEditor.tsx`) fuehren einen Meldungs-Callback. Ihn nachzuruesten hiesse, zwei
 * Props-Schnittstellen UND `App.tsx` anzufassen - ein eigener Vorgang, und `App.tsx` steht gerade
 * in anderer Arbeit. Offene Aufgabe, keine Nachlaessigkeit.
 *
 * ================= Dieselbe Luecke im Bestell-Reiter (Festlegung, nicht vergessen) =================
 *
 * `Drawer/MainTab/Positionen.tsx#Positionen` traegt einen byte-identischen Griff
 * (`className="item-drag-handle"`, `draggable="true"`, ohne Handler) mit derselben fehlenden
 * Verdrahtung. Eine gemeinsame Datei fuer beide Tabellen waere die sauberere Loesung - seit dem
 * Merge `b4f1f60` (2026-08-17) liegt `Drawer/**` wieder im selben Baum wie dieses Modul. Das
 * Herausziehen in einen gemeinsamen Ort ist trotzdem der naechste Schritt, nicht dieser - die
 * Funktionen unten sind bewusst generisch (`<T>`) gehalten, um diesen Schritt nicht zu erschweren.
 *
 * ================= Verallgemeinert fuer VorlagenDialog.tsx (Auftrag "Standardaufgaben lassen sich
 * umsortieren", 2026-08-27) =================
 *
 * Genau der oben angekuendigte naechste Schritt, aber fuer den ZWEITEN, NICHT-tabellenbasierten
 * Verwendungsfall aus demselben Kopfkommentar: `VorlagenDialog.tsx` rendert seine Zeilen als
 * `<div className="task-template-row">`, nicht als `<table>`/`<tr>` - `tabellenWurzel()` (`.closest
 * ("table")`), der feste Zeilenselektor `"tr"` (`positionZiehenStart`) und der feste
 * `data-item-scope`-Wert `'new-order'` (`positionZielFokussieren`) griffen dafuer nicht.
 *
 * Die vier oeffentlichen Funktionen, die einen dieser drei Werte brauchten, nehmen jetzt ein
 * optionales letztes Argument `optionen?: PositionenZiehenOptionen` entgegen (`wurzelSelektor`,
 * `zeilenSelektor`, `scope` - VORGABE_OPTIONEN unten entspricht exakt dem alten, fest verdrahteten
 * Verhalten). `PositionsEditor.tsx` (der einzige BESTEHENDE Aufrufer) ruft sie weiterhin OHNE dieses
 * Argument auf und ist an dieser Datei UNVERAENDERT - genau die Auftragsvorgabe ("ohne
 * PositionsEditor.tsx ... zu veraendern"). Ein Optionen-Objekt statt drei einzelner Positionsargumente,
 * weil drei gleichartige optionale Zeichenketten-Parameter am Funktionsende sonst an der Aufrufstelle
 * nicht mehr auseinanderzuhalten waeren (`fn(a, b, c, "table", "tr")` liest sich nicht von selbst).
 *
 * `Drawer/MainTab/Positionen.tsx` (siehe Abschnitt "Dieselbe Luecke im Bestell-Reiter" oben) bleibt
 * unveraendert unverdrahtet - ausdruecklich NICHT Teil dieses Auftrags, eine eigene Baustelle.
 */

/** Traegt den Ziehzustand ueber `dragstart` -> `dragover` -> `drop`/`dragend` hinweg. */
export interface PositionenZiehenRef {
  /** Der Index der gezogenen Position, `null` ausserhalb eines Ziehvorgangs. */
  index: number | null;
  /** Der Index der zuletzt ueberfahrenen Zeile - `itemDragState.targetIndex` im Original. */
  zielIndex: number | null;
  /** Ablegen NACH der Zielzeile (untere Haelfte) statt davor. */
  danach: boolean;
}

export function neuePositionenZiehenRef(): PositionenZiehenRef {
  return { index: null, zielIndex: null, danach: false };
}

/**
 * Die drei generalisierbaren Werte (siehe Kopfkommentar, "Verallgemeinert fuer
 * VorlagenDialog.tsx") - jedes Feld optional, jeder Aufrufer ohne dieses Argument erhaelt
 * `VORGABE_OPTIONEN`, also 1:1 das alte, fest verdrahtete Verhalten.
 */
export interface PositionenZiehenOptionen {
  /** DOM-Wurzel, in der `raeumeAuf` sucht (bisher immer `"table"`, `.closest(...)`). */
  wurzelSelektor?: string;
  /** Zeilenselektor ab dem Griff, fuer die `item-dragging`-Klasse (bisher immer `"tr"`). */
  zeilenSelektor?: string;
  /** `data-item-scope`-Wert der `dataTransfer`-Nutzlast und von `positionZielFokussieren`
   *  (bisher immer `"new-order"`). */
  scope?: string;
}

const VORGABE_OPTIONEN: Required<PositionenZiehenOptionen> = {
  wurzelSelektor: "table",
  zeilenSelektor: "tr",
  scope: "new-order",
};

/** `reorderItemArray` (index.html:9690-9698), aber REIN: keine Mutation, `null` statt `false`. */
function reorderItemArray<T>(positionen: readonly T[], von: number, nach: number): T[] | null {
  if (
    !Number.isInteger(von) ||
    !Number.isInteger(nach) ||
    von < 0 ||
    von >= positionen.length ||
    nach < 0 ||
    nach >= positionen.length ||
    von === nach
  ) {
    return null;
  }
  const naechste = positionen.slice();
  const [verschoben] = naechste.splice(von, 1);
  naechste.splice(nach, 0, verschoben);
  return naechste;
}

/**
 * Die DOM-Wurzel - Gegenstueck zu `boardWurzel` in `ZeilenUmzug.ts`. Hiess bis zur Verallgemeinerung
 * `tabellenWurzel` und nahm `"table"` fest an; `selektor` ist der einzige Unterschied.
 */
function wurzelKnoten(knoten: Element, selektor: string): Element | null {
  return knoten.closest(selektor);
}

/**
 * `clearItemDropMarkers` (index.html:9737-9741) plus das Abraeumen von `item-reordering`. Scoped
 * auf die Tabelle, NICHT das ganze Dokument - siehe Kopfkommentar, "Bewusste Abweichung".
 */
function raeumeAuf(wurzel: Element | null): void {
  wurzel
    ?.querySelectorAll(".item-drop-before, .item-drop-after, .item-dragging")
    .forEach((zeile) => zeile.classList.remove("item-drop-before", "item-drop-after", "item-dragging"));
  document.body.classList.remove("item-reordering");
}

/**
 * `startItemDrag` (index.html:9743-9755), am Griff (`dragstart`). Eigenheit 6: bricht ab, wenn am
 * Index kein Element steht - `positionen` kommt dafuer NUR als Existenzpruefung herein, die
 * uebrigen Handler bleiben sonst frei von der eigentlichen Liste.
 */
export function positionZiehenStart<T>(
  ereignis: React.DragEvent<HTMLButtonElement>,
  positionen: readonly T[],
  index: number,
  ref: PositionenZiehenRef,
  optionen?: PositionenZiehenOptionen,
): void {
  if (!positionen[index]) {
    ereignis.preventDefault();
    return;
  }
  const { scope, zeilenSelektor } = { ...VORGABE_OPTIONEN, ...optionen };
  ref.index = index;
  ref.zielIndex = index;
  ref.danach = false;
  // Eigenheit 7: `${scope}:${index}` (index.html:9751), scope fest `'new-order'` fuer den
  // Bestandsaufrufer (PositionsEditor.tsx), verallgemeinert fuer weitere Aufrufer.
  ereignis.dataTransfer?.setData("text/plain", `${scope}:${index}`);
  if (ereignis.dataTransfer) ereignis.dataTransfer.effectAllowed = "move";
  ereignis.currentTarget.closest(zeilenSelektor)?.classList.add("item-dragging");
  document.body.classList.add("item-reordering");
}

/**
 * `previewItemDrop` (index.html:9756-9770), an der Zeile (`dragover`). `HTMLElement` statt
 * `HTMLTableRowElement` (Verallgemeinerung) - `PositionsEditor.tsx`s `<tr onDragOver>` bleibt
 * unveraendert zuweisbar, `VorlagenDialog.tsx`s `<div onDragOver>` jetzt ebenfalls.
 */
export function positionZielUeberfahren(
  ereignis: React.DragEvent<HTMLElement>,
  index: number,
  ref: PositionenZiehenRef,
  optionen?: PositionenZiehenOptionen,
): void {
  if (ref.index === null) return;
  ereignis.preventDefault();
  if (ereignis.dataTransfer) ereignis.dataTransfer.dropEffect = "move";
  const { wurzelSelektor } = { ...VORGABE_OPTIONEN, ...optionen };
  wurzelKnoten(ereignis.currentTarget, wurzelSelektor)
    ?.querySelectorAll(".item-drop-before, .item-drop-after")
    .forEach((zeile) => zeile.classList.remove("item-drop-before", "item-drop-after"));
  const zeile = ereignis.currentTarget;
  const grenzen = zeile.getBoundingClientRect();
  const danach = ereignis.clientY >= grenzen.top + grenzen.height / 2;
  ref.zielIndex = index;
  ref.danach = danach;
  zeile.classList.add(danach ? "item-drop-after" : "item-drop-before");
}

/** `finishItemDrag` (index.html:9771-9775), am Griff (`dragend`) - raeumt IMMER auf. */
export function positionZiehenEnde(
  ereignis: React.DragEvent<HTMLButtonElement>,
  ref: PositionenZiehenRef,
  optionen?: PositionenZiehenOptionen,
): void {
  const { wurzelSelektor } = { ...VORGABE_OPTIONEN, ...optionen };
  raeumeAuf(wurzelKnoten(ereignis.currentTarget, wurzelSelektor));
  ref.index = null;
  ref.zielIndex = null;
  ref.danach = false;
}

/**
 * `dropItemPosition` (index.html:9777-9788), an der Zeile (`drop`). Liefert die vollstaendige neue
 * Liste zurueck (oder `null` bei einer Nichtoperation) - der Aufrufer ruft `onAendern(...)` damit.
 * `HTMLElement` statt `HTMLTableRowElement` - dieselbe Verallgemeinerung wie bei
 * `positionZielUeberfahren` oben.
 */
export function positionAbgelegt<T>(
  ereignis: React.DragEvent<HTMLElement>,
  positionen: readonly T[],
  ref: PositionenZiehenRef,
  optionen?: PositionenZiehenOptionen,
): T[] | null {
  if (ref.index === null) return null;
  ereignis.preventDefault();
  const von = ref.index;
  const zielIndex = ref.zielIndex ?? von;
  const danach = ref.danach;
  const { wurzelSelektor } = { ...VORGABE_OPTIONEN, ...optionen };
  raeumeAuf(wurzelKnoten(ereignis.currentTarget, wurzelSelektor));
  ref.index = null;
  ref.zielIndex = null;
  ref.danach = false;

  let nach = zielIndex + (danach ? 1 : 0);
  if (von < nach) nach--;
  nach = Math.max(0, Math.min(positionen.length - 1, nach));

  return reorderItemArray(positionen, von, nach);
}

export interface PositionTasteErgebnis<T> {
  positionen: T[];
  fokusIndex: number;
}

/**
 * `handleItemReorderKey` + die Zielrechnung aus `moveItemPosition` (index.html:9713-9735), am
 * Griff (`keydown`). ArrowUp/ArrowDown/Home/End; jede andere Taste bleibt unberuehrt.
 */
export function positionTaste<T>(
  ereignis: React.KeyboardEvent<HTMLButtonElement>,
  positionen: readonly T[],
  index: number,
): PositionTasteErgebnis<T> | null {
  if (!positionen.length || !["ArrowUp", "ArrowDown", "Home", "End"].includes(ereignis.key)) {
    return null;
  }
  ereignis.preventDefault();
  const ziel =
    ereignis.key === "Home"
      ? 0
      : ereignis.key === "End"
        ? positionen.length - 1
        : Math.max(0, Math.min(positionen.length - 1, index + (ereignis.key === "ArrowUp" ? -1 : 1)));
  const naechste = reorderItemArray(positionen, index, ziel);
  if (!naechste) return null;
  return { positionen: naechste, fokusIndex: ziel };
}

/**
 * `restoreFocus` (index.html:9719-9723) - NUR der Tastaturweg ruft das (siehe Eigenheit 4 oben).
 * `scope` vorgabemaessig `'new-order'` (PositionsEditor.tsx, unveraendert); ein zweiter Aufrufer
 * (VorlagenDialog.tsx) uebergibt seinen eigenen `data-item-scope`-Wert, damit beide Flaechen sich
 * nicht gegenseitig fokussieren, falls je zwei gleichzeitig im DOM stehen sollten.
 */
export function positionZielFokussieren(zielIndex: number, optionen?: PositionenZiehenOptionen): void {
  const { scope } = { ...VORGABE_OPTIONEN, ...optionen };
  setTimeout(() => {
    document
      .querySelector<HTMLElement>(
        `[data-item-scope="${scope}"][data-item-index="${zielIndex}"] .item-drag-handle`,
      )
      ?.focus();
  }, 0);
}
